import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AudioPlayer } from '../components/AudioPlayer';
import { VoiceSelector } from '../components/VoiceSelector';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';

const MAX_CHARS = 5000;

export function SynthesisPage() {
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState<string | null>(null);
  const [stability, setStability] = useState(0.5);
  const [similarityBoost, setSimilarityBoost] = useState(0.75);
  const { synthesize, synthesizing, audioUrl, error } = useSpeechSynthesis();

  const handleGenerate = async () => {
    if (!text.trim() || !voiceId) return;
    await synthesize({ text: text.trim(), voiceId, stability, similarityBoost });
  };

  const charCount = text.length;
  const overLimit = charCount > MAX_CHARS;

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Text-to-Speech</h1>
        <p className="text-muted-foreground mt-1">
          Convert text to natural-sounding speech using ElevenLabs.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select Voice</CardTitle>
        </CardHeader>
        <CardContent>
          <VoiceSelector selectedVoiceId={voiceId} onSelect={setVoiceId} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Enter Text</CardTitle>
          <CardDescription>Up to {MAX_CHARS.toLocaleString()} characters.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type the text you want to convert to speech…"
              rows={6}
              className="resize-none"
              disabled={synthesizing}
            />
            <p
              className={`text-xs mt-1 text-right ${overLimit ? 'text-destructive' : 'text-muted-foreground'}`}
            >
              {charCount}/{MAX_CHARS}
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-sm">Stability: {stability.toFixed(2)}</Label>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[stability]}
                onValueChange={([v]) => setStability(v)}
                disabled={synthesizing}
              />
              <p className="text-xs text-muted-foreground">
                Higher values produce more consistent, less expressive speech.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Voice Clarity: {similarityBoost.toFixed(2)}</Label>
              <Slider
                min={0}
                max={1}
                step={0.01}
                value={[similarityBoost]}
                onValueChange={([v]) => setSimilarityBoost(v)}
                disabled={synthesizing}
              />
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={!text.trim() || !voiceId || overLimit || synthesizing}
            className="w-full"
          >
            {synthesizing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating…
              </>
            ) : (
              'Generate Speech'
            )}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {audioUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Generated Audio</CardTitle>
          </CardHeader>
          <CardContent>
            <AudioPlayer src={audioUrl} autoPlay />
            <Button variant="outline" size="sm" className="mt-3" asChild>
              <a href={audioUrl} download="speech.mp3">
                Download MP3
              </a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
